import {useAuthStore} from '@/store/auth-store';
import {sessionScope} from '@/features/auth/session-scope';
import { create } from 'zustand';
import { queryClient } from '@/lib/query/query-client';
import { currentAshleySource, newerAshleyGeneration } from './ashley-suggestion-freshness';
import { observeAshleyInteraction, invalidateAshleyInteraction, onAshleyInteraction } from './ashley-interaction';

/**
 * One proactive AI reply suggestion ("Ashley AI suggests") for a conversation.
 * Read through the authorized endpoint after a hint-only `inbox:ai_suggestion`
 * event. The backend anchors it to a persisted inbound. The local interaction
 * generation, cache and generation watermark reject
 * delayed or reordered results. This store never sends a proposal.
 */
export interface AiSuggestion {
  conversationId: string;
  suggestion: string;
  suggestionId?: string;
  confidence?: number;
  model?: string;
  createdAt?: string;
  sourceMessageId?: string;
  sourceMessageCreatedAt?: string;
  contextMessageIds?: string[];
}

interface AiSuggestionState {
  /** conversationId → latest suggestion. A new one for the same conversation
   *  replaces the previous (matches the backend's one-at-a-time semantics). */
  byConversation: Record<string, AiSuggestion>;
  latestGeneration: Record<string, AiSuggestion>;
  setSuggestion(s: AiSuggestion): void;
  clearSuggestion(conversationId: string, expected?:AiSuggestion): void;
  observeInteraction(conversationId: string, messageId?: string, changed?: boolean): void;
}

/**
 * Ephemeral, push-driven UI state — same shape of store as the typing/presence
 * managers. Updates are immutable (new object refs) so component selectors
 * re-render without the tick-token trick the typing store needs for its
 * in-place Map mutations.
 */
export const useAiSuggestionStore = create<AiSuggestionState>((set) => ({
  byConversation: {},
  latestGeneration: {},

  setSuggestion: (s) =>
    set((state) => {
      if (s.sourceMessageId && (!currentAshleySource(s) || !newerAshleyGeneration(s, state.latestGeneration[s.conversationId]))) return {};
      return {
        byConversation: { ...state.byConversation, [s.conversationId]: s },
        latestGeneration: s.sourceMessageId ? { ...state.latestGeneration, [s.conversationId]: s } : state.latestGeneration,
      };
    }),

  observeInteraction: (conversationId, messageId, changed = false) => observeAshleyInteraction(conversationId, messageId, changed),

  clearSuggestion: (conversationId, expected) => {
    const current = useAiSuggestionStore.getState().byConversation[conversationId];
    if (expected && current !== expected) return;
    invalidateAshleyInteraction(conversationId);
  },
}));

/** Hook-friendly selector — the current suggestion for a conversation, or null. */
export function useAiSuggestionFor(
  conversationId: string | null | undefined,
): AiSuggestion | null {
  return useAiSuggestionStore((s) =>
    conversationId ? s.byConversation[conversationId] ?? null : null,
  );
}

useAuthStore.subscribe((next,previous)=>{if(sessionScope(next)!==sessionScope(previous))useAiSuggestionStore.setState({byConversation:{},latestGeneration:{}});});

// Native reads can reveal a missed interaction after reconnect or another
// browser's reply. Retire only the proposal whose loaded source changed.
queryClient.getQueryCache().subscribe(event => {
  const key = event.query.queryKey;
  if (key[0] !== 'messages' || typeof key[1] !== 'string') return;
  const current = useAiSuggestionStore.getState().byConversation[key[1]];
  if (current?.sourceMessageId && !currentAshleySource(current)) useAiSuggestionStore.getState().clearSuggestion(key[1], current);
});

// Invalidation applies even while GET is pending and no card exists yet.
onAshleyInteraction(conversationId => {
  const state = useAiSuggestionStore.getState();
  if (!state.byConversation[conversationId]) return;
  const next = { ...state.byConversation }; delete next[conversationId];
  useAiSuggestionStore.setState({ byConversation: next });
});
